import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Actions from "src/components/actionbar";
import InfiniteScroll from "src/components/infiniteScroll";
import SwitchButton from "src/components/switchButton";
import { RedemptionRequestsDialog } from "../redemptionRequestsDialog";
import RewardCard, { PlaceholderRewardCard } from "../rewardCard";
import { CreateReward } from "../createRewardSlider";
import { DeleteRewardSlider } from "../deleteRewardSlider";
import Toast from "src/components/toast";

import {
  addRewardsToCatalogue,
  fetchGamificationRedemptions,
  fetchGamificationRewards,
  invalidateCreateRewardState,
} from "src/redux/actions/coaching/gamification";
import { RewardResponse } from "src/types/coaching/gamification";

import _ from "lodash";
import styles from "./rewards.module.scss";


export interface GamificationRewardsTabProps {}

const GamificationRewardsTab: React.FC<GamificationRewardsTabProps> = () => {
  const dispatch = useDispatch();
  
  const [rewardsOn, setRewardsOn] = useState(true);
  const [showCreateReward, setShowCreateReward] = useState(false);
  const [editReward, setEditReward] = useState<RewardResponse | null>(null);
  const [deleteReward, setDeleteReward] = useState<RewardResponse | null>(null);
  const [showRedemptionRequests, setShowRedemptionRequests] = useState(false);

  const { loading, rewards, nextPage, hasMoreItems, loaded, error } = useSelector(
    (state: any) => {
      const rewardsState = _.get(state, "coaching.gamification.rewards", {});
      const loading = _.get(rewardsState, "loading");
      const pagination = _.get(rewardsState, "pagination", {});
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems");
      const rewards = _.get(rewardsState, "items") || [];

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage !== null && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading,
        rewards,
        nextPage,
        hasMoreItems,
        loaded: _.get(rewardsState, "loaded", false) as boolean,
        error: _.get(rewardsState, "error", null),
      };
    },
    _.isEqual,
  );

  const { requestTotal, redemptionsLoaded } = useSelector((state: any) => {
    const gamificationState = _.get(state, "coaching.gamification", {});
    const pagination = _.get(gamificationState, "redemptions.pagination", {});
    return {
      requestTotal: _.get(pagination, "totalRecordsCount", 0) as number,
      redemptionsLoaded: _.get(gamificationState, "redemptions.loaded", false) as boolean,
    };
  }, _.isEqual);

  const { createRewardLoaded, createdReward, updateRewardLoaded } = useSelector((state: any) => {
    const createRewardState = _.get(state, "coaching.gamification.createReward", {});
    const updateRewardState = _.get(state, "coaching.gamification.updateReward", {});
    return {
      createRewardLoaded: _.get(createRewardState, "loaded", false) as boolean,
      createdReward: _.get(createRewardState, "item", null),
      updateRewardLoaded: _.get(updateRewardState, "loaded", false) as boolean,
    };
  }, _.isEqual);

  const fetchRewardsForPage = (page: number) => {
    const request = {
      page: page,
      limit: 20,
    };
    dispatch(fetchGamificationRewards(request));
  };

  useEffect(() => {
    fetchRewardsForPage(0);
  }, []);

  useEffect(() => {
    dispatch(fetchGamificationRedemptions({ page: 0, limit: 10 }));
  }, []);

  useEffect(() => {
    if (createRewardLoaded && _.get(createdReward, "id")) {
      dispatch(addRewardsToCatalogue({ records: [createdReward], addAt: "start" }));
      setShowCreateReward(false);
      setEditReward(null);
      fetchRewardsForPage(0);
      toast(
        <Toast.Sucess
          message={`Reward "${_.get(createdReward, "name", "Reward")}" created successfully`}
        />,
        { autoClose: 3000 },
      );
      dispatch(invalidateCreateRewardState());
    }
  }, [createRewardLoaded, createdReward]);

  useEffect(() => {
    if (updateRewardLoaded) {
      setShowCreateReward(false);
      setEditReward(null);
    }
  }, [updateRewardLoaded]);

  const handleLoadMoreRewards = () => {
    if (nextPage !== null) {
      fetchRewardsForPage(nextPage);
    }
  };

  return (
    <div className={styles.rewardsTab}>
      <div className={styles.header}>
        <div className={styles.titles}>
          <h2 className={styles.title}>Rewards Shop</h2>
          <p className={styles.subtext}>
          Manage rewards learners can redeem using earned points.
          </p>
        </div>

        <div className={styles.actions}>
          {/* <div className={styles.rewardsToggle}>
            <SwitchButton
              id="gamification-settings-rewards-on"
              checked={rewardsOn}
              handleCheck={() => setRewardsOn((v) => !v)}
            />
            <span>Rewards on</span>
          </div> */}
          <Actions.Row className={styles.actionsRow}>
            <Actions.Button
              width={3}
              icon="paperflite-add"
              className={styles.addRewardBtn}
              title="add rewards"
              onClick={() => {
                setEditReward(null);
                setShowCreateReward(true);
              }}
            />
            <button
              type="button"
              className={styles.requestsPillBtn}
              title={
                redemptionsLoaded
                  ? `${requestTotal} request${requestTotal === 1 ? "" : "s"}`
                  : "Requests"
              }
              onClick={() => setShowRedemptionRequests(true)}
            >
              <span className={styles.requestsPillCount}>{requestTotal}</span>
              <span className={styles.requestsPillDivider} aria-hidden />
              <span className={styles.requestsPillLabel}>requests</span>
            </button>
          </Actions.Row>
        </div>
      </div>

      {error != null && (
        <p className={styles.fetchError} role="alert">
          Could not load rewards. Please try again.
        </p>
      )}

      <InfiniteScroll
        className={styles.rewardsScroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreRewards}
        loader={() => (
          <div className={styles.grid}>
            {_.times(_.size(rewards) === 0 ? 4 : 1, (i) => (
              <PlaceholderRewardCard key={i} />
            ))}
          </div>
        )}
      >
        {_.size(rewards) === 0 && !loading && loaded && (
          <p className={styles.emptyState}>No rewards yet.</p>
        )}
        {_.size(rewards) > 0 && (
          <div className={styles.grid}>
            {rewards.map((reward: RewardResponse) => (
              <RewardCard
                key={reward.id}
                reward={reward}
                onEdit={(r) => {
                  setEditReward(r);
                  setShowCreateReward(false);
                }}
                onDelete={() => setDeleteReward(reward)}
              />
            ))}
          </div>
        )}
      </InfiniteScroll>

      <CreateReward
        show={showCreateReward || editReward != null}
        rewardToEdit={editReward}
        onClose={() => {
          setShowCreateReward(false);
          setEditReward(null);
        }}
      />

      <DeleteRewardSlider
        show={deleteReward != null}
        reward={deleteReward}
        onClose={() => setDeleteReward(null)}
        onFinish={() => {
          fetchRewardsForPage(0);
          setDeleteReward(null);
        }}
      />

      <RedemptionRequestsDialog
        show={showRedemptionRequests}
        onClose={() => setShowRedemptionRequests(false)}
      />
    </div>
  );
};

export default GamificationRewardsTab;
